import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { DownloadFileType } from '@libs/shared/models';
import { getTrackingPagePath } from '../context';
import { TrackingGoogleService } from '../services';

/**
 * Pushes a generic `download` event to the GTM data layer whenever a user interaction triggers the download of a file (pdf etc.).
 *
 * The service is intentionally generic, so it can be reused for any future tracking of downloads,
 * deriving `fileName` and `fileType` from the download URL and defaulting `location` to the current page path.
 */
@Injectable({ providedIn: 'root' })
export class DownloadTrackingService {
  private readonly document: Document = inject(DOCUMENT);
  private readonly tracking: TrackingGoogleService = inject(TrackingGoogleService);

  /**
   * Tracks a file download.
   *
   * @param url      The URL of the file being downloaded.
   * @param location The page / screen the download was triggered from.
   *                 Defaults to the current location pathname (e.g. `/lmiv/609958`).
   */
  public trackDownload(url: string, location: string = getTrackingPagePath(this.document)): void {
    const fileName: string = this.extractFileName(url);

    this.tracking.trackEvent('download', {
      fileType: this.extractFileType(fileName),
      fileName,
      location,
    });
  }

  /**
   * Extracts the file name (last path segment) from a URL, ignoring any query string or fragment.
   */
  private extractFileName(url: string): string {
    const path: string = url.split(/[?#]/)[0];
    const fileName: string = path.substring(path.lastIndexOf('/') + 1);

    return decodeURIComponent(fileName);
  }

  /**
   * Extracts the lower-cased file extension from a filename, or an empty string when the file name has no extension.
   */
  private extractFileType(fileName: string): DownloadFileType {
    const dotIndex: number = fileName.lastIndexOf('.');

    return (dotIndex > 0 ? fileName.slice(dotIndex + 1).toLowerCase() : '') as DownloadFileType;
  }
}
